import { AnimatePresence, motion } from 'framer-motion';
import debounce from 'lodash.debounce';
import {
  FC,
  PropsWithChildren,
  UIEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { ResponsiveTable } from '@/components/Table';
import { RootState } from '@/utils/store';

const TableWrapper = styled.div`
  position: relative;
  overflow: hidden;
`;

const StyledResponsiveTable = styled(ResponsiveTable)`
  overflow-x: auto;
  position: static;
`;

const Fade = styled.div<{ $transparent: boolean }>`
  pointer-events: none;
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: 68px;
  z-index: 0;
  border-radius: 4px;
  transition: 0.25s ease-out opacity;
  background: linear-gradient(270deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
  opacity: ${({ $transparent }) => ($transparent ? 0 : 1)};
`;

const CustomScrollbarContainer = styled(motion.div)<{ $show: boolean }>`
  position: fixed;
  bottom: 45px;
  right: 27px;
  height: 18px;
  background: ${({ theme }) => theme.colors.white};
  z-index: 2;
  box-shadow: 0px 2px 20px 0px rgba(0, 0, 0, 0.051);
  border-radius: 100px;
  padding: 6px 0;
  display: ${({ $show }) => ($show ? 'block' : 'none')};
`;

const CustomScrollbar = styled.div`
  height: 6px;
  background: #d6d6d6;
  border-radius: 100px;
  width: 0%;
  cursor: pointer;
  position: relative;
  margin-left: 6px;
`;

interface Props {
  className?: string;
}

const ScrollableTable: FC<PropsWithChildren<Props>> = ({
  children,
  className,
}) => {
  const [hasOverflow, setHasOverflow] = useState(false);
  const [isTableEnd, setIsTableEnd] = useState(false);
  const [showScrollbar, setShowScrollbar] = useState(false);
  const [isTableHovered, setIsTableHovered] = useState(false);
  const [scrollbarWidth, setScrollbarWidth] = useState('0%');
  const [scrollbarOffset, setScrollbarOffset] = useState({
    width: 0,
    bottom: 0,
    right: 0,
  });

  const fullScreenModeEnabled = useSelector(
    (rootState: RootState) => rootState.layout.fullScreenModeEnabled,
  );

  const scrollableElementRef = useRef<HTMLDivElement>(null);
  const customScrollbarRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const debouncedScrollCallback = useMemo(
    () =>
      debounce((el: EventTarget & HTMLDivElement) => {
        setIsTableEnd(!(el.scrollWidth - el.scrollLeft - 1 > el.clientWidth));
      }, 100),
    [],
  );

  const onTableScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => debouncedScrollCallback(e.currentTarget),
    [debouncedScrollCallback],
  );

  const checkIfHasOverflow = useCallback(() => {
    if (
      scrollableElementRef.current &&
      (scrollableElementRef.current.offsetHeight <
        scrollableElementRef.current.scrollHeight ||
        scrollableElementRef.current.offsetWidth <
          scrollableElementRef.current.scrollWidth)
    ) {
      return setIsTableEnd(false);
    }
    return setIsTableEnd(true);
  }, []);

  const updateScrollbar = useCallback(() => {
    if (wrapperRef.current) {
      const { offsetWidth } = wrapperRef.current;
      const rect = wrapperRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const wrapperHeight = wrapperRef.current.offsetHeight;
      const offsetBottom = viewportHeight - (rect.top + rect.height);
      const offsetRight = viewportWidth - rect.right;
      setScrollbarOffset({
        width: offsetWidth,
        bottom: offsetBottom,
        right: offsetRight,
      });
      if (wrapperHeight * 1.75 > viewportHeight) {
        setShowScrollbar(true);
      } else {
        setShowScrollbar(false);
      }
    }

    if (scrollableElementRef.current) {
      const { scrollWidth, clientWidth, scrollLeft } =
        scrollableElementRef.current;

      if (scrollWidth > clientWidth) {
        setHasOverflow(true);
        const width = Math.floor((clientWidth / scrollWidth) * 100);
        const left = Math.floor((scrollLeft / scrollWidth) * 100);
        setScrollbarWidth(`${width}%`);
        if (customScrollbarRef.current) {
          customScrollbarRef.current.style.width = `${width}%`;
          customScrollbarRef.current.style.left = `${left}%`;
        }
      } else {
        setHasOverflow(false);
      }
    }
  }, []);

  useEffect(() => {
    checkIfHasOverflow();

    const current = scrollableElementRef.current;
    if (current) {
      const observer = new ResizeObserver(checkIfHasOverflow);
      observer.observe(current);
      return () => observer.disconnect();
    }
  }, [checkIfHasOverflow]);

  useEffect(() => {
    updateScrollbar();

    const scrollableElement = scrollableElementRef.current;

    const handleResize = () => updateScrollbar();

    scrollableElement?.addEventListener('scroll', updateScrollbar);
    window.addEventListener('resize', handleResize);
    document
      .getElementById('dashboard-scrollable-container')
      ?.addEventListener('scroll', updateScrollbar);

    return () => {
      scrollableElement?.removeEventListener('scroll', updateScrollbar);
      window.removeEventListener('resize', handleResize);
      document
        .getElementById('dashboard-scrollable-container')
        ?.removeEventListener('scroll', updateScrollbar);
    };
  }, [
    updateScrollbar,
    children,
    fullScreenModeEnabled,
    isTableHovered,
    showScrollbar,
  ]);

  const onScrollbarMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    const start = event.clientX;
    const initialScrollLeft = scrollableElementRef.current?.scrollLeft ?? 0;

    const dragHandler = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault();
      const deltaX = moveEvent.clientX - start;

      const scrollWidth = scrollableElementRef.current?.scrollWidth ?? 1;
      const clientWidth = scrollableElementRef.current?.clientWidth ?? 1;
      const scrollbarWidthNum = parseInt(scrollbarWidth, 10);
      const maxScroll = scrollWidth - clientWidth;
      const scrollRatio = maxScroll / (30000 / scrollbarWidthNum);

      if (scrollableElementRef.current) {
        scrollableElementRef.current.scrollLeft =
          initialScrollLeft + deltaX * scrollRatio;
      }
      updateScrollbar();
    };

    const stopDrag = () => {
      document.removeEventListener('mousemove', dragHandler);
      document.removeEventListener('mouseup', stopDrag);
    };

    document.addEventListener('mousemove', dragHandler);
    document.addEventListener('mouseup', stopDrag);
  };

  return (
    <div
      ref={wrapperRef}
      onMouseEnter={() => setIsTableHovered(true)}
      onMouseLeave={() => setIsTableHovered(false)}
    >
      <TableWrapper>
        <StyledResponsiveTable
          onScroll={onTableScroll}
          className={className}
          ref={scrollableElementRef}
        >
          {children}
        </StyledResponsiveTable>
        {<Fade $transparent={isTableEnd} />}
      </TableWrapper>
      <AnimatePresence>
        {hasOverflow && showScrollbar && isTableHovered && (
          <CustomScrollbarContainer
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.2 } }}
            exit={{ opacity: 0, transition: { duration: 0.2 } }}
            $show={hasOverflow}
            style={{
              right: scrollbarOffset.right,
              width: scrollbarOffset.width,
              bottom:
                scrollbarOffset.bottom > 0 ? scrollbarOffset.bottom - 5 : 45,
            }}
          >
            <CustomScrollbar
              ref={customScrollbarRef}
              style={{ width: scrollbarWidth }}
              onMouseDown={onScrollbarMouseDown}
            />
          </CustomScrollbarContainer>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ScrollableTable;
